import { Injectable } from '@angular/core';
import { SellPriceConfig } from '../../models/sol-estimate-table/SellPriceConfig';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { EstimateUiModel } from '../../models/sol-estimate-table/ui-models/index';
import { CONSTANTS } from '../../Constants/Constants';
import { roundHalfEven9 } from '../../../types/number-extensions';

/**
 * Handles the sub-totals row of the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 *
 * Sub-totals cost and sell price are always the sum of all header rows above:
 *   Material Costs Header + Labor Costs Header + Subcontractor Header
 *   + Misc/Risk/Travel Header + Tax Not Included.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateSubtotalsService {

  constructor(
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService
  ) {}

  // ── Helpers ────────────────────────────────────────────────────

  /**
   * Sums the cost values of all header rows that feed into the sub-totals.
   */
  private sumHeaderCosts(model: EstimateUiModel): number {
    return (
      model.materialCosts.materialCostsHeader.cost +
      model.laborCosts.laborCostsHeader.cost +
      model.subContractorCosts.headerRow.cost +
      model.miscRiskTravelCosts.headerRow.cost +
      model.taxMaterialRate.headerRow.cost +
      model.technologyFee.cost
    );
  }

  /**
   * Sums the sell-price values of all header rows that feed into the sub-totals.
   */
  private sumHeaderSellPrices(model: EstimateUiModel): number {
    return (
      model.materialCosts.materialCostsHeader.sellPrice +
      model.laborCosts.laborCostsHeader.sellPrice +
      model.subContractorCosts.headerRow.sellPrice +
      model.miscRiskTravelCosts.headerRow.sellPrice +
      model.taxMaterialRate.headerRow.sellPrice +
      model.technologyFee.sellPrice
    );
  }

  /**
   * Writes computed totals into the sub-totals row.
   */
  private applySubTotals(model: EstimateUiModel, config: SellPriceConfig): void {
    const row = model.subTotals;
    const totalCost = this.sumHeaderCosts(model);
    const totalSellPrice = this.sumHeaderSellPrices(model);

    const gmDollars = roundHalfEven9(totalSellPrice - totalCost);
    const presentGmPercent = totalSellPrice === CONSTANTS.ZERO
      ? CONSTANTS.ZERO
      : roundHalfEven9(((totalSellPrice - totalCost) / totalSellPrice) * CONSTANTS.HUNDRED);
    const muPercent = totalCost === CONSTANTS.ZERO
      ? CONSTANTS.ZERO
      : roundHalfEven9(((totalSellPrice - totalCost) / totalCost) * CONSTANTS.HUNDRED);

    const gmPercent = this.estimateCommonFunctions.resolveRowGMPercent(config, presentGmPercent);  

    row.cost = totalCost;
    row.sellPrice = totalSellPrice;
    row.tdcPercentage = CONSTANTS.HUNDRED;

    if (totalCost !== CONSTANTS.ZERO) {
      row.grossMargin = gmDollars;
      row.grossMarginPercentage = gmPercent;
      //ToDo: check if it can be removed. MU%
      row.markUpPercentage = muPercent;
    } else {
      // Zero cost: sell prices that survived zero the margin out, otherwise it stays visible.
      row.grossMargin = CONSTANTS.ZERO;
      if (totalSellPrice !== CONSTANTS.ZERO) {
        row.grossMarginPercentage = CONSTANTS.ZERO;
        row.markUpPercentage = CONSTANTS.ZERO;
      }
    }

    this.estimateCommonFunctions.refreshTdcPercentages(model, totalCost);
  }

  // ── Patching (initial load) ────────────────────────────────────

  patchSubTotalsCost(
    model: EstimateUiModel,
    _config: SellPriceConfig,
  ): void {
    this.applySubTotals(model, _config);
  }

  // ── Recalculation ──────────────────────────────────────────────

  recalculateSubTotals(model: EstimateUiModel, _config: SellPriceConfig): void {
    this.applySubTotals(model, _config);
  }

  // ── Refresh Displayed Percentages (GM↔MU toggle) ──────────────

  refreshSubTotalsPercentages(model: EstimateUiModel): void {
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.subTotals);
  }
}
